import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Film, Play, Download, PlusCircle, X, Loader2, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const toFullUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

export default function VideoGallery() {
  const [videos, setVideos] = useState([]);
  const [selected, setSelected] = useState(null);

  const copyShare = async (slug) => {
    if (!slug) return toast.error("Bu video için paylaşım linki yok");
    const url = `${window.location.origin}/v/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Paylaşım linki kopyalandı");
    } catch {
      toast.error("Link kopyalanamadı");
    }
  };

  useEffect(() => {
    api.get("/videos").then(({ data }) => setVideos(data)).catch(() => {});
  }, []);

  return (
    <div className="p-6 md:p-12 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#C29B62] mb-2">Kütüphane</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Videolarım</h1>
        </div>
        <Link to="/dashboard/create" data-testid="gallery-create-cta"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[#C29B62] text-slate-950 font-semibold hover:bg-[#D4AF7A] transition">
          <PlusCircle size={18} /> Yeni Video
        </Link>
      </div>

      {videos.length === 0 ? (
        <div className="py-24 text-center rounded-xl bg-slate-900/40 border border-white/10">
          <Film size={48} className="mx-auto text-slate-700 mb-4" />
          <p className="text-slate-400">Henüz video oluşturmadınız.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map(v => (
            <div key={v.video_id} data-testid={`video-card-${v.video_id}`}
              className="rounded-xl overflow-hidden border border-white/10 hover:border-[#C29B62]/40 bg-slate-900/40 transition group">
              <button onClick={() => v.status === "ready" && setSelected(v)} disabled={v.status !== "ready"} className="relative aspect-video w-full block">
                <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
                {v.status === "processing" ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-950/60">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#C29B62]/20 border border-[#C29B62]/40 text-[#C29B62] text-sm">
                      <Loader2 size={16} className="animate-spin" /> Sora 2 üretiyor…
                    </div>
                  </div>
                ) : v.status === "failed" ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-red-950/40">
                    <span className="text-red-300 text-sm">Üretim başarısız</span>
                  </div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                    <div className="w-14 h-14 rounded-full bg-[#C29B62] flex items-center justify-center">
                      <Play size={22} className="text-slate-950 ml-1" />
                    </div>
                  </div>
                )}
              </button>
              <div className="p-5">
                <div className="text-xs uppercase tracking-[0.25em] text-[#C29B62]">{v.il} / {v.ilce}</div>
                <div className="font-semibold mt-2">Ada {v.ada} · Parsel {v.parsel}</div>
                <div className="text-xs text-slate-500 mt-1">{v.company_name}</div>
                <div className="flex items-center gap-2 mt-4">
                  <a href={toFullUrl(v.video_url)} download data-testid={`download-${v.video_id}`}
                    className={`flex-1 py-2 rounded-md border border-white/15 hover:border-[#C29B62]/60 hover:bg-white/5 transition text-xs text-center inline-flex items-center justify-center gap-1.5 ${v.status !== "ready" ? "opacity-40 pointer-events-none" : ""}`}>
                    <Download size={14} /> İndir
                  </a>
                  <button onClick={() => copyShare(v.share_slug)}
                    data-testid={`share-${v.video_id}`}
                    disabled={v.status !== "ready" || !v.share_slug}
                    className={`flex-1 py-2 rounded-md border border-[#C29B62]/40 text-[#C29B62] hover:bg-[#C29B62]/10 transition text-xs inline-flex items-center justify-center gap-1.5 ${(v.status !== "ready" || !v.share_slug) ? "opacity-40 cursor-not-allowed hover:bg-transparent" : ""}`}>
                    <LinkIcon size={14} /> Paylaş
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} className="absolute -top-10 right-0 text-slate-400 hover:text-white">
              <X size={24} />
            </button>
            <div className="rounded-xl overflow-hidden border border-[#C29B62]/30">
              <video src={toFullUrl(selected.video_url)} poster={selected.thumbnail_url} controls autoPlay className="w-full aspect-[9/16] bg-black max-w-[420px] mx-auto block" />
            </div>
            <div className="mt-4 p-5 rounded-xl bg-slate-900/80 border border-white/10">
              <div className="text-xs uppercase tracking-[0.25em] text-[#C29B62] mb-2">Pazarlama Metni</div>
              <p className="text-slate-200 text-sm leading-relaxed">{selected.narration}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
